/**
 * Вкладка «Задачи»: отбор по чату, статусу, «кто взял» и тексту; задачи по приоритетам П0–П3 в порядке доски.
 * В строке — кто взял и как давно; «Взять» у свободной открытой задачи, «Отпустить» — у своей (владельцу портала —
 * у любой). Если задачу успели взять раньше — сообщение сервера с именем взявшего и обновление списка. Список
 * приходит не длиннее предела сервера: если подходит больше — «Показаны первые N из M — уточните отбор».
 */
import {
  BOARD_PRIO_HINTS,
  BOARD_PRIOS,
  BOARD_STATUS_TITLES,
  BOARD_STATUSES,
  type BoardChat,
  type BoardMe,
  type BoardTakeResult,
  type BoardTask,
  type BoardTaskList,
} from '@epc/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { plural } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { BOARD_API } from './access';
import {
  type BoardSearch,
  isClosed,
  PRIO_TONE,
  StatusBadge,
  takerText,
  useBoardError,
  useRefreshBoard,
} from './shared';

export function useTakeRelease() {
  const refresh = useRefreshBoard();
  const onError = useBoardError();
  const take = useMutation({
    mutationFn: (id: string) =>
      api<BoardTakeResult>(`${BOARD_API}/tasks/${encodeURIComponent(id)}/take`, { method: 'POST' }),
    onSuccess: (t) => {
      toast(t.alreadyYours ? `Задача уже ваша: ${t.title}` : `Задача ваша: ${t.title}`);
      refresh();
    },
    onError,
  });
  const release = useMutation({
    mutationFn: (id: string) =>
      api<BoardTask>(`${BOARD_API}/tasks/${encodeURIComponent(id)}/release`, { method: 'POST', body: {} }),
    onSuccess: () => {
      toast('Задача снова свободна');
      refresh();
    },
    onError,
  });
  return { take, release };
}

/** Можно ли человеку на странице отпустить задачу: свою или (владельцу портала) любую */
export const canRelease = (t: BoardTask, me: BoardMe) =>
  Boolean(t.takenByUserId) && (t.takenByUserId === me.userId || me.isOwner);
export const canTake = (t: BoardTask) => !t.takenByUserId && !isClosed(t.status);

function TaskRow({ t, me, onOpen }: { t: BoardTask; me: BoardMe; onOpen: (id: string) => void }) {
  const { take, release } = useTakeRelease();
  const mine = t.takenByUserId === me.userId && !t.takenByChat;
  return (
    <li className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-start md:gap-4 md:px-5">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge tone={PRIO_TONE[t.prio]}>{t.prio}</Badge>
          {t.chat ? <Badge tone="ink">{t.chat}</Badge> : <Badge>без чата</Badge>}
          <StatusBadge status={t.status} />
          {t.ref ? <span className="text-[12px] text-ink-3 [overflow-wrap:anywhere]">{t.ref}</span> : null}
        </div>
        <button
          type="button"
          onClick={() => onOpen(t.id)}
          className="mt-1 inline-flex min-h-11 w-full items-center text-left text-[15px] leading-5 font-medium [overflow-wrap:anywhere] text-ink underline-offset-2 hover:underline md:min-h-0 md:py-0.5"
        >
          {t.title}
        </button>
        <div className="flex flex-col gap-0.5 text-[13px] leading-[18px] text-ink-2">
          <span className={clsx(t.takenByUserId ? 'text-ink' : 'text-ink-2', mine && 'font-medium')}>
            {takerText(t)}
          </span>
          {t.reqs.length || t.branch || t.notesCount ? (
            <span className="[overflow-wrap:anywhere]">
              {[
                t.reqs.length ? t.reqs.join(', ') : null,
                t.branch ? `ветка ${t.branch}` : null,
                t.notesCount ? plural(t.notesCount, 'комментарий', 'комментария', 'комментариев') : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          ) : null}
          {t.blocker ? (
            <span className="text-warning [overflow-wrap:anywhere]">Ждёт: {t.blocker}</span>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:justify-end">
        {canTake(t) ? (
          <Button size="sm" variant="secondary" loading={take.isPending} onClick={() => take.mutate(t.id)}>
            Взять
          </Button>
        ) : null}
        {canRelease(t, me) ? (
          <Button size="sm" variant="ghost" loading={release.isPending} onClick={() => release.mutate(t.id)}>
            Отпустить
          </Button>
        ) : null}
      </div>
    </li>
  );
}

/** Поиск в адрес — после паузы в наборе, чтобы список не перезапрашивался на каждую букву */
function SearchInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [text, setText] = useState(value);
  useEffect(() => setText(value), [value]);
  useEffect(() => {
    if (text === value) return;
    const t = setTimeout(() => onChange(text), 350);
    return () => clearTimeout(t);
  }, [text, value, onChange]);
  return (
    <Input
      id="board-q"
      type="search"
      value={text}
      placeholder="Название, требование, ветка"
      onChange={(e) => setText(e.target.value)}
    />
  );
}

export function TasksTab({
  me,
  search,
  setSearch,
  onOpen,
}: {
  me: BoardMe;
  search: BoardSearch;
  setSearch: (patch: Partial<BoardSearch>) => void;
  onOpen: (id: string) => void;
}) {
  const filter = {
    chat: search.chat,
    status: search.status ?? 'open',
    taken: search.taken ?? 'any',
    q: search.q,
  };
  const tasks = useQuery({
    queryKey: ['devboard', 'tasks', filter],
    queryFn: () => api<BoardTaskList>(`${BOARD_API}/tasks`, { query: filter }),
    // Чаты берут задачи сами: список освежается, пока страница открыта
    refetchInterval: 60_000,
  });
  const list = tasks.data?.tasks;
  const chats = useQuery({
    queryKey: ['devboard', 'chats'],
    queryFn: () => api<BoardChat[]>(`${BOARD_API}/chats`),
  });
  const chatCodes = useMemo(() => {
    const codes = new Set((chats.data ?? []).map((c) => c.code));
    for (const t of list ?? []) if (t.chat) codes.add(t.chat);
    if (search.chat && search.chat !== 'none') codes.add(search.chat);
    return [...codes].sort();
  }, [chats.data, list, search.chat]);
  const groups = useMemo(
    () => BOARD_PRIOS.map((p) => ({ prio: p, items: (list ?? []).filter((t) => t.prio === p) })),
    [list],
  );
  const setQ = useMemo(() => (v: string) => setSearch({ q: v.trim() || undefined }), [setSearch]);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[repeat(3,minmax(0,200px))_minmax(0,1fr)]">
        <Field label="Чат" htmlFor="board-chat">
          <Select
            id="board-chat"
            value={search.chat ?? ''}
            onChange={(e) => setSearch({ chat: e.target.value || undefined })}
          >
            <option value="">Все чаты</option>
            <option value="none">Без чата</option>
            {chatCodes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Статус" htmlFor="board-status">
          <Select
            id="board-status"
            value={search.status ?? 'open'}
            onChange={(e) => setSearch({ status: e.target.value === 'open' ? undefined : e.target.value })}
          >
            <option value="open">Открытые</option>
            <option value="all">Все</option>
            {BOARD_STATUSES.map((s) => (
              <option key={s} value={s}>
                {BOARD_STATUS_TITLES[s]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Кто взял" htmlFor="board-taken">
          <Select
            id="board-taken"
            value={search.taken ?? 'any'}
            onChange={(e) =>
              setSearch({ taken: e.target.value === 'any' ? undefined : (e.target.value as 'free' | 'mine') })
            }
          >
            <option value="any">Все</option>
            <option value="free">Свободные</option>
            <option value="mine">Взятые мной</option>
          </Select>
        </Field>
        <Field label="Поиск" htmlFor="board-q">
          <SearchInput value={search.q ?? ''} onChange={setQ} />
        </Field>
      </div>

      {tasks.isPending ? (
        <Spinner />
      ) : tasks.isError ? (
        <ErrorBox error={tasks.error} />
      ) : !tasks.data.tasks.length ? (
        <EmptyState title="Задач нет">
          {search.chat || search.q || search.taken || search.status
            ? 'По этому отбору задач нет — смените отбор.'
            : 'Новые задачи заводят кнопкой «Новая задача», чаты — командой node scripts/board.mjs add.'}
        </EmptyState>
      ) : (
        <>
          {tasks.data.truncated ? (
            <p
              role="status"
              className="rounded-md border border-warning/40 bg-warning-soft px-3 py-2 text-[13px] text-ink"
            >
              Показаны первые {tasks.data.tasks.length} из {tasks.data.total} — уточните отбор.
            </p>
          ) : null}
          {groups
            .filter((g) => g.items.length)
            .map((g) => (
              <Panel
                key={g.prio}
                title={`${g.prio} — ${BOARD_PRIO_HINTS[g.prio]}`}
                meta={plural(g.items.length, 'задача', 'задачи', 'задач')}
                bodyClassName="p-0"
              >
                <ul className="divide-y divide-line">
                  {g.items.map((t) => (
                    <TaskRow key={t.id} t={t} me={me} onOpen={onOpen} />
                  ))}
                </ul>
              </Panel>
            ))}
        </>
      )}
    </div>
  );
}
